import constants from '@/plugins/constants';

const { API_URLS } = constants;

function unwrap(response, fallbackMessage = 'Request failed') {
  const body = response?.data;
  if (body?.status === 'success') {
    return body.data !== undefined ? body.data : body;
  }
  const message = body?.message || fallbackMessage;
  const error = new Error(message);
  error.code = body?.code;
  error.response = response;
  throw error;
}

function domainPath(id, suffix = '') {
  return `${API_URLS.SSO_DOMAINS}/${encodeURIComponent(id)}${suffix}`;
}

function connectionPath(id, suffix = '') {
  return `${API_URLS.SSO_CONNECTIONS}/${encodeURIComponent(id)}${suffix}`;
}

/** Viewed tenant in the path (not body/query `client`) so tenant-binding uses the session client. */
function withViewedClient(path, clientId) {
  return `${path}/${encodeURIComponent(clientId)}`;
}

function withoutBindingClient(payload = {}) {
  const rest = { ...payload };
  delete rest.client;
  delete rest.client_id;
  return rest;
}

export default {
  namespaced: true,
  state: () => ({
    domain: null,
    domains: [],
    connections: [],
    loading: false,
    saving: false,
    error: null,
    bootstrapStatus: null,
  }),
  getters: {
    primaryDomain: (state) => {
      const list = Array.isArray(state.domains) ? state.domains : [];
      return (
        list.find((d) => d?.isPrimary && d?.lifecycle !== 'RETIRED') ||
        list.find((d) => d?.lifecycle !== 'RETIRED') ||
        list[0] ||
        null
      );
    },
    activeHostname: (state, getters) => {
      const d = state.domain || getters.primaryDomain;
      return d?.hostname || d?.primaryHost || '';
    },
  },
  mutations: {
    setDomain(state, domain) {
      state.domain = domain || null;
    },
    setDomains(state, domains) {
      state.domains = Array.isArray(domains) ? domains : [];
    },
    setConnections(state, connections) {
      state.connections = Array.isArray(connections) ? connections : [];
    },
    setLoading(state, value) {
      state.loading = !!value;
    },
    setSaving(state, value) {
      state.saving = !!value;
    },
    setError(state, error) {
      state.error = error || null;
    },
    setBootstrapStatus(state, status) {
      state.bootstrapStatus = status || null;
    },
    reset(state) {
      state.domain = null;
      state.domains = [];
      state.connections = [];
      state.loading = false;
      state.saving = false;
      state.error = null;
      state.bootstrapStatus = null;
    },
  },
  actions: {
    async fetchDomains({ commit }, { axiosInstance, clientId }) {
      if (!clientId) {
        commit('setDomains', []);
        return [];
      }
      commit('setLoading', true);
      commit('setError', null);
      try {
        const response = await axiosInstance.post(
          withViewedClient(`${API_URLS.SSO_DOMAINS}/list`, clientId),
          {},
        );
        const data = unwrap(response, 'Error fetching SSO domains');
        const list = Array.isArray(data) ? data : data?.domains || data?.items || [];
        commit('setDomains', list);
        const primary =
          list.find((d) => d?.isPrimary && d?.lifecycle !== 'RETIRED') ||
          list.find((d) => d?.lifecycle !== 'RETIRED') ||
          list[0] ||
          null;
        commit('setDomain', primary);
        return list;
      } catch (error) {
        commit('setError', error.message || 'Error fetching SSO domains');
        commit('setDomains', []);
        throw error;
      } finally {
        commit('setLoading', false);
      }
    },

    async fetchDomain({ commit }, { axiosInstance, domainId }) {
      if (!domainId) return null;
      commit('setLoading', true);
      commit('setError', null);
      try {
        const response = await axiosInstance.get(domainPath(domainId));
        const data = unwrap(response, 'Error fetching SSO domain');
        const domain = data?.domain || data;
        commit('setDomain', domain);
        return domain;
      } catch (error) {
        commit('setError', error.message || 'Error fetching SSO domain');
        throw error;
      } finally {
        commit('setLoading', false);
      }
    },

    async bootstrapDomain({ commit }, { axiosInstance, clientId, payload }) {
      commit('setSaving', true);
      commit('setError', null);
      try {
        const response = await axiosInstance.post(
          withViewedClient(API_URLS.SSO_DOMAINS_BOOTSTRAP, clientId),
          withoutBindingClient({
            ...payload,
            clientIds: payload?.clientIds || (clientId ? [clientId] : undefined),
          }),
        );
        const data = unwrap(response, 'Error bootstrapping SSO domain');
        commit('setBootstrapStatus', data);
        return data;
      } catch (error) {
        commit('setError', error.message || 'Error bootstrapping SSO domain');
        throw error;
      } finally {
        commit('setSaving', false);
      }
    },

    async updateDomain({ commit }, { axiosInstance, domainId, clientId, payload }) {
      commit('setSaving', true);
      commit('setError', null);
      try {
        const response = await axiosInstance.post(
          domainPath(domainId, `/update/${encodeURIComponent(clientId)}`),
          withoutBindingClient(payload),
        );
        const data = unwrap(response, 'Error updating SSO domain');
        const domain = data?.domain || data;
        if (domain && typeof domain === 'object') {
          commit('setDomain', domain);
        }
        return data;
      } catch (error) {
        commit('setError', error.message || 'Error updating SSO domain');
        throw error;
      } finally {
        commit('setSaving', false);
      }
    },

    async addDomain({ commit }, { axiosInstance, clientId, payload }) {
      commit('setSaving', true);
      commit('setError', null);
      try {
        const response = await axiosInstance.post(
          withViewedClient(`${API_URLS.SSO_DOMAINS}/create`, clientId),
          withoutBindingClient({
            ...payload,
            clientIds: payload?.clientIds || (clientId ? [clientId] : undefined),
          }),
        );
        return unwrap(response, 'Error creating SSO domain');
      } catch (error) {
        commit('setError', error.message || 'Error creating SSO domain');
        throw error;
      } finally {
        commit('setSaving', false);
      }
    },

    async retireDomain({ commit }, { axiosInstance, domainId, clientId, reason, reserveDays }) {
      commit('setSaving', true);
      commit('setError', null);
      try {
        const response = await axiosInstance.post(
          domainPath(domainId, `/retire/${encodeURIComponent(clientId)}`),
          withoutBindingClient({ reason, reserveDays }),
        );
        return unwrap(response, 'Error retiring SSO domain');
      } catch (error) {
        commit('setError', error.message || 'Error retiring SSO domain');
        throw error;
      } finally {
        commit('setSaving', false);
      }
    },

    async addClientId({ commit }, { axiosInstance, domainId, clientId, targetClientId }) {
      commit('setSaving', true);
      commit('setError', null);
      try {
        const response = await axiosInstance.post(
          domainPath(domainId, `/clientIds/add/${encodeURIComponent(targetClientId || clientId)}`),
          {},
        );
        return unwrap(response, 'Error adding client to domain');
      } catch (error) {
        commit('setError', error.message || 'Error adding client to domain');
        throw error;
      } finally {
        commit('setSaving', false);
      }
    },

    async removeClientId({ commit }, { axiosInstance, domainId, clientId, targetClientId }) {
      commit('setSaving', true);
      commit('setError', null);
      try {
        const response = await axiosInstance.post(
          domainPath(
            domainId,
            `/clientIds/remove/${encodeURIComponent(targetClientId || clientId)}`,
          ),
          {},
        );
        return unwrap(response, 'Error removing client from domain');
      } catch (error) {
        commit('setError', error.message || 'Error removing client from domain');
        throw error;
      } finally {
        commit('setSaving', false);
      }
    },

    async fetchConnections({ commit }, { axiosInstance, hostname, clientId }) {
      if (!hostname || !clientId) {
        commit('setConnections', []);
        return [];
      }
      commit('setLoading', true);
      commit('setError', null);
      try {
        const response = await axiosInstance.post(
          withViewedClient(`${API_URLS.SSO_CONNECTIONS}/list`, clientId),
          { hostname },
        );
        const data = unwrap(response, 'Error fetching SSO connections');
        const list = Array.isArray(data)
          ? data
          : data?.connections || data?.items || [];
        commit('setConnections', list);
        return list;
      } catch (error) {
        commit('setError', error.message || 'Error fetching SSO connections');
        commit('setConnections', []);
        throw error;
      } finally {
        commit('setLoading', false);
      }
    },

    async createConnection({ commit }, { axiosInstance, clientId, payload }) {
      commit('setSaving', true);
      commit('setError', null);
      try {
        const response = await axiosInstance.post(
          withViewedClient(`${API_URLS.SSO_CONNECTIONS}/create`, clientId),
          withoutBindingClient(payload),
        );
        return unwrap(response, 'Error creating SSO connection');
      } catch (error) {
        commit('setError', error.message || 'Error creating SSO connection');
        throw error;
      } finally {
        commit('setSaving', false);
      }
    },

    async updateConnection({ commit }, { axiosInstance, connectionId, clientId, payload }) {
      commit('setSaving', true);
      commit('setError', null);
      try {
        const response = await axiosInstance.post(
          connectionPath(connectionId, `/update/${encodeURIComponent(clientId)}`),
          withoutBindingClient(payload),
        );
        return unwrap(response, 'Error updating SSO connection');
      } catch (error) {
        commit('setError', error.message || 'Error updating SSO connection');
        throw error;
      } finally {
        commit('setSaving', false);
      }
    },

    async testConnection({ commit }, { axiosInstance, connectionId, clientId }) {
      commit('setSaving', true);
      commit('setError', null);
      try {
        const response = await axiosInstance.post(
          connectionPath(connectionId, `/test/${encodeURIComponent(clientId)}`),
          {},
        );
        return unwrap(response, 'Error testing SSO connection');
      } catch (error) {
        commit('setError', error.message || 'Error testing SSO connection');
        throw error;
      } finally {
        commit('setSaving', false);
      }
    },

    async activateConnection({ commit }, { axiosInstance, connectionId, clientId }) {
      commit('setSaving', true);
      commit('setError', null);
      try {
        const response = await axiosInstance.post(
          connectionPath(connectionId, `/activate/${encodeURIComponent(clientId)}`),
          {},
        );
        return unwrap(response, 'Error activating SSO connection');
      } catch (error) {
        commit('setError', error.message || 'Error activating SSO connection');
        throw error;
      } finally {
        commit('setSaving', false);
      }
    },

    async disableConnection({ commit }, { axiosInstance, connectionId, clientId }) {
      commit('setSaving', true);
      commit('setError', null);
      try {
        const response = await axiosInstance.post(
          connectionPath(connectionId, `/disable/${encodeURIComponent(clientId)}`),
          {},
        );
        return unwrap(response, 'Error disabling SSO connection');
      } catch (error) {
        commit('setError', error.message || 'Error disabling SSO connection');
        throw error;
      } finally {
        commit('setSaving', false);
      }
    },

    async retireConnection(
      { commit },
      { axiosInstance, connectionId, clientId, reason, confirm = true },
    ) {
      commit('setSaving', true);
      commit('setError', null);
      try {
        const response = await axiosInstance.post(
          connectionPath(connectionId, `/retire/${encodeURIComponent(clientId)}`),
          withoutBindingClient({ reason, confirm }),
        );
        return unwrap(response, 'Error retiring SSO connection');
      } catch (error) {
        commit('setError', error.message || 'Error retiring SSO connection');
        throw error;
      } finally {
        commit('setSaving', false);
      }
    },
  },
};
